import React, { useMemo } from 'react';

export interface TrendDatum {
    label: string;
    value: number;
}

export interface DistributionSlice {
    label: string;
    value: number;
}

const euroFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const TREND_VIEWBOX_WIDTH = 100;
const TREND_VIEWBOX_HEIGHT = 60;
const TREND_VERTICAL_PADDING = 6;

export const CashflowTrendChart: React.FC<{ data: TrendDatum[] }> = ({ data }) => {
    const points = useMemo(() => {
        if (!data || data.length === 0) {
            return [] as Array<{ x: number; y: number; label: string; value: number }>;
        }
        const sanitized = data.map((item) => ({ ...item, value: Number.isFinite(item.value) ? item.value : 0 }));
        const maxValue = Math.max(...sanitized.map(item => item.value), 0);
        const horizontalStep = sanitized.length > 1 ? TREND_VIEWBOX_WIDTH / (sanitized.length - 1) : 0;
        const verticalRange = TREND_VIEWBOX_HEIGHT - TREND_VERTICAL_PADDING * 2;
        return sanitized.map((item, index) => {
            const ratio = maxValue > 0 ? item.value / maxValue : 0;
            const x = index * horizontalStep;
            const y = TREND_VIEWBOX_HEIGHT - TREND_VERTICAL_PADDING - (ratio * verticalRange);
            return { x, y, label: item.label, value: item.value };
        });
    }, [data]);

    if (points.length === 0) {
        return <div className="chart-empty">Nessun dato da mostrare</div>;
    }

    if (points.length === 1) {
        const point = points[0];
        return (
            <svg className="trend-chart" viewBox={`0 0 ${TREND_VIEWBOX_WIDTH} ${TREND_VIEWBOX_HEIGHT}`} role="img" aria-label="Trend incassi">
                <circle cx={point.x} cy={point.y} r={2} className="trend-point" />
            </svg>
        );
    }

    const linePath = points.reduce((accumulator, point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${accumulator} ${command}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
    }, '').trim();

    return (
        <svg className="trend-chart" viewBox={`0 0 ${TREND_VIEWBOX_WIDTH} ${TREND_VIEWBOX_HEIGHT}`} role="img" aria-label="Trend incassi">
            <path d={linePath} className="trend-line" />
            {points.map(point => (
                <circle key={`${point.label}-${point.x}`} cx={point.x} cy={point.y} r={2} className="trend-point" />
            ))}
        </svg>
    );
};

const DONUT_COLORS = ['#2563eb', '#22c55e', '#f97316', '#a855f7', '#ec4899', '#0ea5e9', '#facc15'];

export const CashflowDistributionChart: React.FC<{ data: DistributionSlice[] }> = ({ data }) => {
    const { slices, total } = useMemo(() => {
        if (!data || data.length === 0) {
            return { slices: [] as Array<{ label: string; value: number; color: string; percentage: number }>, total: 0 };
        }
        const sanitized = data
            .map((item, index) => ({
                label: item.label,
                value: Number.isFinite(item.value) && item.value > 0 ? item.value : 0,
                color: DONUT_COLORS[index % DONUT_COLORS.length],
            }))
            .filter(item => item.value > 0);
        const aggregatedTotal = sanitized.reduce((acc, item) => acc + item.value, 0);
        if (aggregatedTotal === 0) {
            return { slices: [] as Array<{ label: string; value: number; color: string; percentage: number }>, total: 0 };
        }
        return {
            slices: sanitized.map(item => ({
                ...item,
                percentage: Number(((item.value / aggregatedTotal) * 100).toFixed(1)),
            })),
            total: aggregatedTotal,
        };
    }, [data]);

    if (!slices.length || total === 0) {
        return <div className="chart-empty">Nessun dato disponibile</div>;
    }

    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
        <div className="donut-chart-wrapper">
            <svg className="donut-chart" viewBox="0 0 120 120" role="img" aria-label="Distribuzione incassi">
                <circle className="donut-background" cx="60" cy="60" r={radius} />
                {slices.map(slice => {
                    const dash = (slice.value / total) * circumference;
                    const element = (
                        <circle
                            key={slice.label}
                            className="donut-segment"
                            cx="60"
                            cy="60"
                            r={radius}
                            stroke={slice.color}
                            strokeWidth={14}
                            fill="transparent"
                            strokeDasharray={`${dash} ${circumference - dash}`}
                            strokeDashoffset={-offset}
                        />
                    );
                    offset += dash;
                    return element;
                })}
                <text x="60" y="60" className="donut-center" textAnchor="middle">
                    €
                </text>
            </svg>
            <ul className="donut-legend">
                {slices.map(slice => (
                    <li key={slice.label}>
                        <span className="legend-color" style={{ backgroundColor: slice.color }} />
                        <span className="legend-label">{slice.label}</span>
                        <span className="legend-value">{euroFormatter.format(slice.value)} ({slice.percentage}%)</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
